import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { Component, computed, effect, inject, input } from '@angular/core';
import { PersonNameService } from '#services';
import { EngineCaseStore } from '../engine-case.store';
import { auditPersonOids, labelFor, priorOwnerOid, roleLabel, toRailStages } from '../engine-case.mapper';
import { EngineStepPanelComponent, StepDecision } from '../engine-step-panel/engine-step-panel.component';
import { Uc2LifecycleRailComponent } from '../../components/uc2-lifecycle-rail/uc2-lifecycle-rail.component';
import { WorkflowAuditTrailComponent } from '../../components/workflow-audit-trail/workflow-audit-trail.component';
import { WorkflowPublishedGraphComponent } from '../../components/workflow-published-graph/workflow-published-graph.component';
import { buildAuditTrail } from '../../mappers/audit-trail.mapper';
import { buildUc2EngineView, uc2AnalystReturn } from '../../mappers/engine-well-exception.mapper';
import { usesRealWorkflowEngine, WORKFLOW_ENGINE_CONFIGURATION } from '../../../../../services/workflow-engine/provide-workflow-engine-gateway';

@Component({
  selector: 'app-well-exception-live-workflow',
  imports: [DatePipe, NgTemplateOutlet, EngineStepPanelComponent, Uc2LifecycleRailComponent, WorkflowAuditTrailComponent, WorkflowPublishedGraphComponent],
  providers: [EngineCaseStore],
  templateUrl: './well-exception-live-workflow.component.html',
  styleUrls: [
    '../../workflow-detail.scss',
    '../../components/well-exception-workflow/well-exception-workflow.component.scss',
  ],
})
export class WellExceptionLiveWorkflowComponent {
  caseId = input.required<string>();
  readonly store = inject(EngineCaseStore);
  private _names = inject(PersonNameService);
  private _configuration = inject(WORKFLOW_ENGINE_CONFIGURATION);
  liveEnabled = computed(() => usesRealWorkflowEngine(this._configuration));
  readonly labelFor = labelFor;
  readonly roleLabel = roleLabel;
  state = computed(() => this.store.composed()?.state ?? null);
  entries = computed(() => this.store.composed()?.audit.entries ?? []);
  view = computed(() => buildUc2EngineView(this.store.composed()?.dossier ?? null, this.entries(), this.store.form()));
  displayedCandidates = computed(() => {
    const detail = this.view();
    return detail.selectedCandidate ? [detail.selectedCandidate] : detail.candidates;
  });
  candidateHeading = computed(() => this.view().selectedCandidate ? 'Selected candidate' : 'Candidate explanations');
  inlineExecution = computed(() =>
    this.store.form()?.stepKey === 'execute_actions' && !!this.view().selectedCandidate && !this.editingCandidate(),
  );
  actionDescriptionShown = computed(() => {
    const item = this.store.form()?.item;
    return this.inlineExecution() && !!item?.description?.trim()
      && !!this.view().selectedCandidate?.recommendedActions.some(action =>
        action.id === item.id && action.description.trim() === item.description?.trim(),
      );
  });
  editingCandidate = computed(() =>
    (this.store.canClaim() || this.store.canDecide()) &&
    this.store.fields().some(field => field.name === 'selectedCandidate' && field.type === 'ref'),
  );
  railStages = computed(() => toRailStages(this.store.graph()));
  ownerName = computed(() => this._names.nameOf(this.state()?.ownerOid));
  returnOwner = computed(() => {
    const target = this.store.form()?.return?.to;
    return target ? priorOwnerOid(this.entries(), target) : null;
  });
  returnName = computed(() => this._names.nameOf(this.returnOwner()));
  analystReturn = computed(() => this.store.isOpen()
    ? uc2AnalystReturn(this.entries(), this.state()?.activeStep?.stepKey) : null);
  returnedBy = computed(() => this._names.nameOf(this.analystReturn()?.actorOid) ?? 'Name unavailable');
  auditTrail = computed(() => {
    const item = this.store.form()?.item;
    return buildAuditTrail({
      entries: this.entries(), nodes: this.store.composed()?.graph.nodes ?? [],
      candidateTitles: Object.fromEntries(this.view().candidates.map(candidate => [candidate.id, candidate.label])),
      itemRoles: item?.id && item.targetRole ? { [item.id]: item.targetRole } : {},
      nameOf: oid => this._names.nameOf(oid),
    });
  });
  suggestions = computed(() => this.store.composed()?.dossier?.signals.filter(signal => signal.attachMode === 'suggested') ?? []);

  constructor() {
    effect(() => {
      if (this.liveEnabled()) this.store.load(this.caseId());
    });
    effect(() => this._names.request([
      this.state()?.ownerOid, this.returnOwner(), ...auditPersonOids(this.entries()),
    ]));
  }

  onDecide(decision: StepDecision): void {
    this.store.decide(decision.outcome, decision.values, decision.reason, { claimStartStep: true });
  }
}
